import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { PublicUser, StaffRole, StandingLevel } from '@spawnwire/shared';
import { Avatar, Button, clsx, Modal, TextArea } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { LEVEL_INFO } from '../../lib/moderation';

export function PageHeader({ title, description, children }: { title: string; description?: string; children?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-fg">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function LevelBadge({ level }: { level: StandingLevel }) {
  const info = LEVEL_INFO[level];
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-full bg-main px-2 py-0.5 text-xs font-semibold', info.tone)}>
      <span className={clsx('size-1.5 rounded-full', info.bar)} aria-hidden />
      {info.label}
    </span>
  );
}

export function StaffBadge({ role }: { role: StaffRole | null }) {
  if (!role) return null;
  return <span className={clsx('rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide', role === 'admin' ? 'bg-danger text-white' : 'bg-accent text-accent-fg')}>{role}</span>;
}

/** A person, linking to their admin page. */
export function UserChip({ user, size = 24, sub }: { user: PublicUser | null; size?: number; sub?: ReactNode }) {
  const navigate = useNavigate();
  if (!user) return <span className="text-sm text-faint">Unknown</span>;
  return (
    <button type="button" onClick={() => navigate(`/app/admin/users/${user.id}`)} className="flex min-w-0 items-center gap-2 rounded-md text-left hover:underline">
      <Avatar user={user} size={size} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-fg">{user.displayName}</span>
        <span className="block truncate text-xs text-muted">@{user.username}{sub}</span>
      </span>
    </button>
  );
}

export function Tabs<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div role="tablist" className="inline-flex rounded-lg bg-main p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx('rounded-md px-3 py-1 text-sm font-medium', value === o.value ? 'bg-active text-fg' : 'text-muted hover:text-fg')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('rounded-xl border border-line bg-sidebar p-4', className)}>{children}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-xl border border-dashed border-line p-8 text-center text-sm text-muted">{children}</p>;
}

/** Asks for a reason (or a reply) before a staff action, and runs it. */
export function ReasonDialog({
  title,
  description,
  label = 'Reason',
  placeholder,
  confirmLabel,
  danger,
  optional,
  onSubmit,
  onClose,
}: {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  confirmLabel: string;
  danger?: boolean;
  optional?: boolean;
  onSubmit: (text: string) => Promise<void>;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(text.trim());
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={title} description={description}>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor="admin-reason">
          {label}
        </label>
        <TextArea id="admin-reason" rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder={placeholder} autoFocus />
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant={danger ? 'danger' : 'primary'} loading={busy} disabled={!optional && text.trim().length < 3}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
